import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { LifecycleEntityType, LifecycleEvent } from "@adva/types";
import { resolveLifecycleMarker } from "@/lib/lifecycle-config";
import type { LifecycleTimelineItem } from "@/components/v2/timeline/lifecycle-timeline";

// ADV-1347 (lifecycle epic): reads GET /core/entity-lifecycle — the DB-first
// per-entity milestone projection (core.entity_lifecycle_v). Replaces the
// client deriver for jobs; the opportunity keeps its own deriver (a separate
// ticket migrates it). Mirrors useEntityActivity.
//
// The view owns ALL milestone selection (one row per reached milestone). The
// hook does no aggregation — it only resolves each row's marker (iconKey +
// tint) from LIFECYCLE_CONFIG so the LifecycleTimeline stays a dumb renderer.

interface ListResponse {
  lifecycle_events: LifecycleEvent[];
  total: number;
}

/**
 * Map a server `LifecycleEvent` row into the timeline's already-resolved
 * `LifecycleTimelineItem` shape. The view returns rows oldest-first; preserve
 * that order so the rail reads earliest → latest.
 */
function toTimelineItem(event: LifecycleEvent): LifecycleTimelineItem {
  return {
    id: `${event.entity_type}-${event.event_type}-${event.entity_id}`,
    title: event.title,
    time: event.occurred_at,
    detail: event.detail,
    marker: resolveLifecycleMarker(
      event.entity_type,
      event.event_type,
      event.stage,
    ),
  };
}

export function useEntityLifecycle(
  entityType: LifecycleEntityType,
  id: string | undefined,
) {
  const qs = new URLSearchParams();
  if (id) {
    qs.set("entity_type", entityType);
    qs.set("entity_id", id);
  }

  return useQuery({
    queryKey: ["entity-lifecycle", entityType, id],
    queryFn: async () => {
      const data = await api.get<ListResponse>(
        `/core/entity-lifecycle?${qs.toString()}`,
      );
      return {
        items: data.lifecycle_events.map(toTimelineItem),
        total: data.total,
      };
    },
    enabled: !!id,
  });
}
